import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { supabase } from "@/integrations/supabase/client";
import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { formatCurrency } from "@/lib/currency";
import { Calendar, Clock, MapPin, ArrowLeft, MessageSquare, Image as ImageIcon } from "lucide-react";

type BookingRow = {
  id: string;
  booking_number: string | null;
  status: string;
  created_at: string;
  precheck?: any;
  service_address_text?: string | null;
  billing_address_text?: string | null;
  scheduled_date: string | null;
  scheduled_time: string | null;
  confirmed_at?: string | null;
  start_at?: string | null;
  finish_at?: string | null;
  completed_at?: string | null;
  cancel_reason?: string | null;
  reschedule_reason?: string | null;
  notes?: string | null;
  total_amount?: number | null;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  companies?: { company_name?: string | null; logo_url?: string | null } | null;
  services?: { name?: string | null } | null;
  service_addresses?: { address_line1?: string | null; address_line2?: string | null; city?: string | null; postal_code?: string | null; country?: string | null } | null;
};

type PhotoRow = { id: string; url: string; photo_type: "pre" | "post" | "evidence"; created_at: string };
type MessageRow = { id: string; author_role: string; body: string; created_at: string };

function statusLabel(s: string) {
  switch (s) {
    case "pending": return "In attesa";
    case "confirmed": return "Confermato";
    case "in_progress": return "In corso";
    case "completed": return "Completato";
    case "cancelled": return "Cancellato";
    default: return s;
  }
}

function statusVariant(s: string) {
  if (s === "completed") return "secondary";
  if (s === "cancelled") return "destructive";
  return "default";
}

function safeFileName(name: string) {
  return name.replace(/[^\w.\-]+/g, "_");
}

function hasMeaningfulPrecheck(p: { access_instructions: string; has_pets: string; parking: string; notes: string }) {
  if ((p.access_instructions || "").trim()) return true;
  if ((p.notes || "").trim()) return true;
  if ((p.has_pets || "unknown") !== "unknown") return true;
  if ((p.parking || "unknown") !== "unknown") return true;
  return false;
}

export default function BookingDetail() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState<BookingRow | null>(null);
  const [role, setRole] = useState<"customer" | "vendor">("customer");

  const [photos, setPhotos] = useState<(PhotoRow & { signedUrl?: string | null })[]>([]);
  const [uploading, setUploading] = useState(false);
  const [photoType, setPhotoType] = useState<PhotoRow["photo_type"]>("pre");
  const [file, setFile] = useState<File | null>(null);

  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [sending, setSending] = useState(false);
  const [messageText, setMessageText] = useState("");

  const [precheck, setPrecheck] = useState<{
    access_instructions: string;
    has_pets: "yes" | "no" | "unknown";
    parking: "easy" | "limited" | "none" | "unknown";
    notes: string;
  }>({ access_instructions: "", has_pets: "unknown", parking: "unknown", notes: "" });
  const [savingPrecheck, setSavingPrecheck] = useState(false);

  const precheckFilled = useMemo(() => hasMeaningfulPrecheck(precheck), [precheck]);

  const address = useMemo(() => {
    const sa = booking?.service_addresses;
    if (sa) {
      return [sa.address_line1, sa.address_line2, `${sa.postal_code || ""} ${sa.city || ""}`.trim(), sa.country]
        .filter(Boolean)
        .join(", ");
    }
    // Fallback for guest bookings (no service_address_id)
    return String(booking?.service_address_text || "").trim();
  }, [booking]);

  const timeline = useMemo(() => {
    if (!booking) return [];
    const items: { label: string; at: string | null; note?: string }[] = [
      { label: "Creata", at: booking.created_at },
      { label: "Confermata", at: booking.confirmed_at || null },
      { label: "Inizio (check-in)", at: booking.start_at || null },
      { label: "Fine (check-out)", at: booking.finish_at || null },
      { label: "Completata", at: booking.completed_at || null },
    ];
    if (booking.status === "cancelled") {
      items.push({ label: "Cancellata", at: booking.updated_at || booking.created_at, note: booking.cancel_reason || undefined } as any);
    }
    return items.filter(i => i.at);
  }, [booking]);

  const refreshPhotos = async (bid: string) => {
    const { data } = await (supabase as any)
      .from("booking_photos")
      .select("id,url,photo_type,created_at")
      .eq("booking_id", bid)
      .order("created_at", { ascending: false })
      .limit(100);
    const rows = (data || []) as PhotoRow[];
    const mapped = await Promise.all(rows.map(async (p) => {
      // Old rows may contain a public URL; new rows store the storage path in booking-photos bucket.
      if (/^https?:\/\//i.test(p.url)) return { ...p, signedUrl: p.url };
      try {
        const { data: signed } = await (supabase as any).storage.from("booking-photos").createSignedUrl(p.url, 60 * 60);
        return { ...p, signedUrl: signed?.signedUrl || null };
      } catch {
        return { ...p, signedUrl: null };
      }
    }));
    setPhotos(mapped);
  };

  const refreshMessages = async (bid: string) => {
    const { data } = await (supabase as any)
      .from("booking_messages")
      .select("id, author_role, body, created_at")
      .eq("booking_id", bid)
      .order("created_at", { ascending: true })
      .limit(300);
    setMessages((data || []) as any);
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const sess = await supabase.auth.getSession();
        const uid = sess.data.session?.user?.id;
        if (!uid) { navigate("/auth"); return; }
        if (!bookingId) { navigate("/profile"); return; }

        const { data: prof } = await (supabase as any)
          .from("profiles")
          .select("user_type")
          .eq("user_id", uid)
          .maybeSingle();
        const r: "customer" | "vendor" = (prof?.user_type === "company") ? "vendor" : "customer";
        if (active) setRole(r);

        const { data: bk, error } = await (supabase as any)
          .from("bookings")
          .select("id, booking_number, status, created_at, updated_at, precheck, service_address_text, billing_address_text, scheduled_date, scheduled_time, confirmed_at, start_at, finish_at, completed_at, cancel_reason, reschedule_reason, notes, total_amount, customer_fee_cents, pricing_snapshot, customer_name, customer_email, customer_phone, companies(company_name,logo_url), services(name), service_addresses(address_line1,address_line2,city,postal_code,country)")
          .eq("id", bookingId)
          .maybeSingle();
        if (error || !bk) {
          toast({ title: "Prenotazione non trovata", description: "Non hai accesso a questa prenotazione o non esiste.", variant: "destructive" });
          navigate("/profile");
          return;
        }
        if (active) setBooking(bk as any);
        try {
          const pc = (bk as any)?.precheck || {};
          if (active) {
            setPrecheck({
              access_instructions: String(pc.access_instructions || ""),
              has_pets: (pc.has_pets === "yes" || pc.has_pets === "no" || pc.has_pets === "unknown") ? pc.has_pets : "unknown",
              parking: (pc.parking === "easy" || pc.parking === "limited" || pc.parking === "none" || pc.parking === "unknown") ? pc.parking : "unknown",
              notes: String(pc.notes || "")
            });
          }
        } catch {}

        await refreshPhotos(String(bookingId));
        await refreshMessages(String(bookingId));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const savePrecheck = async () => {
    if (!bookingId) return;
    setSavingPrecheck(true);
    try {
      // Customer-only write path; vendor sees read-only.
      if (role !== "customer") {
        toast({ title: "Solo cliente", description: "La checklist pre-servizio può essere compilata dal cliente.", variant: "destructive" });
        return;
      }
      const payload = {
        access_instructions: precheck.access_instructions.trim(),
        has_pets: precheck.has_pets,
        parking: precheck.parking,
        notes: precheck.notes.trim(),
        updated_at: new Date().toISOString()
      };
      const { error } = await (supabase as any).rpc("customer_update_booking_precheck", {
        p_booking_id: bookingId,
        p_precheck: payload
      });
      if (error) throw error;
      setBooking((prev) => prev ? ({ ...prev, precheck: payload } as any) : prev);
      toast({ title: "Salvato", description: "Checklist pre-servizio aggiornata." });
    } catch (e: any) {
      toast({ title: "Salvataggio fallito", description: e?.message || "Riprova.", variant: "destructive" });
    } finally {
      setSavingPrecheck(false);
    }
  };

  const uploadPhoto = async () => {
    if (!bookingId || !file) return;
    setUploading(true);
    try {
      const safe = safeFileName(file.name);
      const path = `${bookingId}/${Date.now()}-${photoType}-${safe}`;
      const up = await (supabase as any).storage.from("booking-photos").upload(path, file, { upsert: false });
      if (up.error) throw up.error;
      const ins = await (supabase as any).from("booking_photos").insert({ booking_id: bookingId, url: path, photo_type: photoType });
      if (ins.error) throw ins.error;
      setFile(null);
      await refreshPhotos(String(bookingId));
      toast({ title: "Foto caricata", description: "La foto è stata aggiunta alla prenotazione." });
    } catch (e: any) {
      toast({ title: "Upload fallito", description: e?.message || "Impossibile caricare la foto", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const sendMessage = async () => {
    if (!bookingId || !messageText.trim()) return;
    setSending(true);
    try {
      const sess = await supabase.auth.getSession();
      const uid = sess.data.session?.user?.id;
      if (!uid) throw new Error("not_authenticated");

      const ins = await (supabase as any).from("booking_messages").insert({
        booking_id: bookingId,
        author_user_id: uid,
        author_role: role,
        body: messageText.trim()
      });
      if (ins.error) throw ins.error;
      setMessageText("");
      await refreshMessages(String(bookingId));
    } catch (e: any) {
      toast({ title: "Messaggio non inviato", description: e?.message || "Riprova.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen app-page">
      <Seo title="Dettaglio prenotazione | Halomora" noindex canonicalPath={bookingId ? `/booking/${bookingId}` : "/booking"} />
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate(role === "vendor" ? "/company/dashboard?tab=bookings" : "/profile")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Indietro
          </Button>
          {booking?.booking_number && (
            <Badge variant="outline">#{booking.booking_number}</Badge>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-gray-500">Caricamento…</div>
        ) : !booking ? (
          <div className="text-sm text-gray-500">Prenotazione non trovata.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-gray-600">Dettaglio prenotazione</div>
                    <div className="text-xl font-semibold text-gray-900 truncate">
                      {booking.services?.name || "Servizio"} — {booking.companies?.company_name || "Azienda"}
                    </div>
                  </div>
                  <Badge variant={statusVariant(booking.status)}>{statusLabel(booking.status)}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Make precheck more evident (especially for vendor) */}
                {precheckFilled && (
                  <div className={`rounded-xl border p-4 ${role === "vendor" ? "border-primary/30 bg-primary/5" : "border-gray-200 bg-white"}`}>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className={role === "vendor" ? "bg-primary/10 text-primary" : ""}>
                            Info dal cliente
                          </Badge>
                          <span className="text-sm text-gray-700">
                            Checklist pre-servizio compilata
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Animali: <span className="font-medium text-gray-900">{precheck.has_pets === "yes" ? "Sì" : precheck.has_pets === "no" ? "No" : "—"}</span>
                          {"  •  "}
                          Parcheggio: <span className="font-medium text-gray-900">
                            {precheck.parking === "easy" ? "Facile" : precheck.parking === "limited" ? "Limitato" : precheck.parking === "none" ? "Assente" : "—"}
                          </span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => document.getElementById("precheck")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                      >
                        Vai alla checklist
                      </Button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-600">Data</div>
                      <div className="font-medium text-gray-900">
                        {booking.scheduled_date ? format(new Date(booking.scheduled_date), "dd/MM/yyyy") : "—"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <div className="text-sm text-gray-600">Orario</div>
                      <div className="font-medium text-gray-900">{booking.scheduled_time || "—"}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 md:col-span-2">
                    <MapPin className="h-5 w-5 text-primary mt-0.5" />
                    <div className="min-w-0">
                      <div className="text-sm text-gray-600">Indirizzo</div>
                      <div className="font-medium text-gray-900 break-words">{address || "—"}</div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  {(() => {
                    const _service = Number(booking.total_amount || 0);
                    const _feeC = Math.max(0, Number((booking as any).customer_fee_cents || 0));
                    const _fee = _feeC / 100;
                    const _vendorCovers = !!(booking as any).pricing_snapshot?.vendor_covers_customer_fee;
                    const _customerPaid = _service + (_vendorCovers ? 0 : _fee);
                    return (
                      <>
                        <div className="text-sm text-gray-600">Totale</div>
                        <div className="text-2xl font-bold text-gray-900">{formatCurrency(_customerPaid)}</div>
                        {_fee > 0 && (
                          <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                            <div>Servizio: <span className="font-medium">{formatCurrency(_service)}</span></div>
                            <div>
                              Fee piattaforma: <span className="font-medium">{formatCurrency(_fee)}</span>
                              {_vendorCovers && <span className="ml-1 text-emerald-700">(coperta dal fornitore)</span>}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {!!booking.notes && (
                  <div>
                    <div className="text-sm text-gray-600">Note</div>
                    <div className="mt-1 rounded-lg border bg-white p-3 text-gray-900 whitespace-pre-wrap">{booking.notes}</div>
                  </div>
                )}

                <Separator />

                <div id="precheck">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-lg font-semibold text-gray-900">Checklist pre-servizio</div>
                    <Badge variant="outline" className={precheckFilled ? "border-primary/40 text-primary" : ""}>
                      {precheckFilled ? "Compilata" : "Da compilare"}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-600">Aiuta l’azienda a prepararsi (accesso, animali, parcheggio, note).</div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <div className="text-sm text-gray-600 mb-1">Istruzioni accesso (citofono, interno, chiavi, ecc.)</div>
                      <Textarea
                        value={precheck.access_instructions}
                        onChange={(e) => setPrecheck((p) => ({ ...p, access_instructions: e.target.value }))}
                        placeholder="Es. Citofono Rossi, interno 3B. Le chiavi sono dal portiere…"
                        disabled={role !== "customer"}
                      />
                    </div>

                    <div>
                      <div className="text-sm text-gray-600 mb-1">Animali in casa</div>
                      <Select
                        value={precheck.has_pets}
                        onValueChange={(v) => setPrecheck((p) => ({ ...p, has_pets: v as any }))}
                        disabled={role !== "customer"}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unknown">Non specificato</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="yes">Sì</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <div className="text-sm text-gray-600 mb-1">Parcheggio</div>
                      <Select
                        value={precheck.parking}
                        onValueChange={(v) => setPrecheck((p) => ({ ...p, parking: v as any }))}
                        disabled={role !== "customer"}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleziona" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unknown">Non specificato</SelectItem>
                          <SelectItem value="easy">Facile</SelectItem>
                          <SelectItem value="limited">Limitato</SelectItem>
                          <SelectItem value="none">Assente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="md:col-span-2">
                      <div className="text-sm text-gray-600 mb-1">Note aggiuntive</div>
                      <Textarea
                        value={precheck.notes}
                        onChange={(e) => setPrecheck((p) => ({ ...p, notes: e.target.value }))}
                        placeholder="Es. Zone da evitare, attenzione a superfici delicate…"
                        disabled={role !== "customer"}
                      />
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-2">
                    <Button onClick={savePrecheck} disabled={role !== "customer" || savingPrecheck}>
                      {savingPrecheck ? "Salvataggio…" : "Salva checklist"}
                    </Button>
                    {role !== "customer" && (
                      <Badge variant="outline">solo lettura</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Timeline</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {timeline.length === 0 ? (
                    <div className="text-sm text-gray-500">Nessun evento ancora.</div>
                  ) : (
                    <div className="space-y-2">
                      {timeline.map((t) => (
                        <div key={t.label} className="flex items-start justify-between gap-3">
                          <div className="text-sm font-medium text-gray-900">{t.label}</div>
                          <div className="text-sm text-gray-600">{t.at ? format(new Date(t.at), "dd/MM HH:mm") : ""}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" />
                    Foto
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 gap-2">
                    <Select value={photoType} onValueChange={(v) => setPhotoType(v as any)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Tipo foto" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pre">Prima</SelectItem>
                        <SelectItem value="post">Dopo</SelectItem>
                        <SelectItem value="evidence">Prova</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    <Button disabled={uploading || !file} onClick={uploadPhoto}>
                      {uploading ? "Caricamento…" : "Carica foto"}
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {photos.slice(0, 6).map((p) => (
                      <div key={p.id} className="relative rounded-lg overflow-hidden border bg-white">
                        {p.signedUrl ? (
                          <img src={p.signedUrl} alt={p.photo_type} className="w-full h-28 object-cover" />
                        ) : (
                          <div className="w-full h-28 flex items-center justify-center text-xs text-gray-500">Immagine non disponibile</div>
                        )}
                        <div className="absolute top-2 left-2">
                          <Badge variant="outline" className="bg-white/90">{p.photo_type}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    Messaggi
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2 max-h-56 overflow-auto border rounded-lg bg-white p-3">
                    {messages.length === 0 ? (
                      <div className="text-sm text-gray-500">Nessun messaggio ancora.</div>
                    ) : (
                      messages.map((m) => (
                        <div key={m.id} className="text-sm">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-gray-900">{m.author_role}</span>
                            <span className="text-xs text-gray-500">{format(new Date(m.created_at), "dd/MM HH:mm")}</span>
                          </div>
                          <div className="text-gray-900 whitespace-pre-wrap">{m.body}</div>
                        </div>
                      ))
                    )}
                  </div>
                  <Textarea value={messageText} onChange={(e) => setMessageText(e.target.value)} placeholder="Scrivi un messaggio…" />
                  <Button disabled={sending || !messageText.trim()} onClick={sendMessage}>
                    {sending ? "Invio…" : "Invia"}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

